import { Trans } from '@lingui/react';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import TermsAndConditionsViewerDialog from 'components/TermsAndConditionsGate/TermsAndConditionsViewerDialog';
import React from 'react';

interface AccountMenuProps {
  logout: () => void;
  name: string;
  isLarge: boolean;
}
const AccountMenu = ({ logout, name }: AccountMenuProps) => {
  const [accountMenuOpen, setAccountMenuOpen] = React.useState(false);
  const [termsDialogOpen, setTermsDialogOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const handleAccountMenuToggle = () => {
    setAccountMenuOpen((prevOpen) => !prevOpen);
  };

  const handleAccountMenuClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setAccountMenuOpen(false);
  };

  const handlePrivacyPolicyClick = () => {
    setAccountMenuOpen(false);
    window.open('https://minesmart.io/privacy-policy', '_blank');
  };

  const handleTermsAndConditionsClick = () => {
    setAccountMenuOpen(false);
    setTermsDialogOpen(true);
  };

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      setAccountMenuOpen(false);
    }
  };

  // return focus to the button when we transitioned from !accountMenuOpen -> accountMenuOpen
  const prevAccountMenuOpen = React.useRef(accountMenuOpen);
  React.useEffect(() => {
    if (prevAccountMenuOpen.current === true && accountMenuOpen === false) {
      anchorRef.current!.focus();
    }

    prevAccountMenuOpen.current = accountMenuOpen;
  }, [accountMenuOpen]);

  return (
    (
      <>
        <Button
          ref={anchorRef}
          variant="outlined"
          aria-label="account of current user"
          aria-controls={accountMenuOpen ? 'menu-list-grow' : undefined}
          aria-haspopup="true"
          onClick={handleAccountMenuToggle}
          color="inherit"
        >
          <Typography noWrap>{name}</Typography>
        </Button>
        <Popper open={accountMenuOpen} anchorEl={anchorRef.current} role={undefined} transition disablePortal>
          {({ TransitionProps, placement }) => (
            <Grow {...TransitionProps} style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}>
              <Paper>
                <ClickAwayListener onClickAway={handleAccountMenuClose}>
                  <MenuList autoFocusItem={accountMenuOpen} id="menu-list-grow" onKeyDown={handleListKeyDown}>
                    <MenuItem component={Link} href="/ChangePassword" color="textPrimary">
                      <Trans id="Change Password">Change Password</Trans>
                    </MenuItem>
                    <MenuItem onClick={handlePrivacyPolicyClick} color="textPrimary">
                      <Trans id="Privacy & Cookie Policy">Privacy & Cookie Policy</Trans>
                    </MenuItem>
                    <MenuItem onClick={handleTermsAndConditionsClick} color="textPrimary">
                      <Trans id="Terms and Conditions">Terms and Conditions</Trans>
                    </MenuItem>
                    <MenuItem onClick={logout}><Trans id="Logout">Logout</Trans></MenuItem>
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
        <TermsAndConditionsViewerDialog
          open={termsDialogOpen}
          onClose={() => setTermsDialogOpen(false)}
        />
      </>
    )
  );
};

export default AccountMenu;
